import { useParams, Link } from 'react-router-dom';
import { categories, featuredProducts } from '@/mocks/products';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const categoryProducts = featuredProducts.filter((p) => p.category === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">请检查分类链接是否正确</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '分类导航', href: '/category/keji-dianzi' },
            { label: category.name },
          ]}
        />

        <section className="w-full relative h-[300px] md:h-[380px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          <div className="relative z-10 text-center px-4">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
              <i className={`${category.icon} text-2xl md:text-3xl text-white`}></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-lg mx-auto">
              {category.description}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">快速切换：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              精选{category.name}好物
            </h2>

            {categoryProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {categoryProducts.map((product) => (
                  <div
                    key={product.id}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="relative w-full aspect-square overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`${product.name} - 精品国产推荐`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {product.badge && (
                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-background-50 dark:text-foreground-950 text-xs font-medium rounded-full">
                          {product.badge}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                          {product.brand}
                        </span>
                      </div>
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 mb-1.5 line-clamp-2 leading-snug">
                        {product.name}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-base font-semibold text-accent-600">
                          {product.price}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          <span>{product.rating}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-background-100 rounded-lg">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无推荐产品，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        {categoryArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                {category.name}相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}